import React, { useState } from 'react';
import {
  Question,
  QuestionsComponentProps,
} from '../../../../../interfaces/interfaceSteps';
import QuestionImg from '../../../../../assets/images/questions.svg';
import { sendFirstQuestionnaire } from '../../../../../config/services/steps/sendFirstQuestionnaire';
import { sendRepechageQuestion } from '../../../../../config/services/steps/sendRepechageQuestion';
import FinalStepContent from '../../final-step/FinalStepContent';
import './QuestionsComponent.css';

const QuestionsComponent: React.FC<QuestionsComponentProps> = ({
  preguntas,
  hash,
  onQuestionsCompleted,
  onFinalStepReached,
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<
    { id: string; pregunta: string; respuesta: string }[]
  >([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newQuestion, setNewQuestion] = useState<Question | null>(null);
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [finalStatus, setFinalStatus] = useState<'success' | 'error' | null>(
    null
  );
  const [isFinalStep, setIsFinalStep] = useState(false);
  const [responseCode, setResponseCode] = useState<string>('');
  const [responseHash, setResponseHash] = useState<string>('');

  const updateFinalStep = (value: boolean) => {
    setIsFinalStep(value);
    if (onFinalStepReached) {
      onFinalStepReached(value);
    }
  };

  const handleAnswerChange = (alternativa: string) => {
    const currentQuestion = newQuestion || preguntas[currentQuestionIndex];
    const updatedAnswers = [...answers];
    const existingAnswerIndex = updatedAnswers.findIndex(
      (answer) => answer.id === currentQuestion.id
    );

    if (existingAnswerIndex > -1) {
      updatedAnswers[existingAnswerIndex].respuesta = alternativa;
    } else {
      updatedAnswers.push({
        id: currentQuestion.id,
        pregunta: currentQuestion.pregunta,
        respuesta: alternativa,
      });
    }

    setAnswers(updatedAnswers);
  };

  const handleNext = async () => {
    if (newQuestion) {
      const respuestaRepechaje = answers.find(
        (ans) => ans.id === newQuestion.id
      );
      if (!respuestaRepechaje) {
        setAlertMessage('Por favor selecciona una respuesta para continuar.');
        return;
      }

      const payload = {
        hash,
        respuestas: [respuestaRepechaje],
      };

      try {
        setIsSubmitting(true);
        const response = await sendRepechageQuestion(
          payload.hash,
          payload.respuestas
        );

        if (response?.data) {
          if (response.data.codigo === 'KBA09') {
            updateFinalStep(true);
            setFinalStatus('success');
            setResponseCode(response.data.codigo);
            setResponseHash(response.data.hash);

            setNewQuestion(null);
            onQuestionsCompleted({
              hash,
              respuestas: answers,
              isApproved: true,
            });
          } else {
            setFinalStatus('error');
            setResponseCode(response.data.codigo);
            setResponseHash(response.data.hash);
            updateFinalStep(true);
          }
        } else {
          setAlertMessage('No se recibió una respuesta válida del servidor.');
        }
      } catch (error) {
        console.error('Error en repechaje:', error);
        setAlertMessage('Hubo un problema al procesar tu respuesta.');
        setResponseCode('ERROR');
        updateFinalStep(true);
      } finally {
        setIsSubmitting(false);
      }
    } else if (currentQuestionIndex < preguntas.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      const payload = {
        hash,
        respuestas: answers,
      };

      try {
        setIsSubmitting(true);
        const response = await sendFirstQuestionnaire(
          payload.hash,
          payload.respuestas
        );

        if (response?.data) {
          if (response.data.codigo === 'KBA03') {
            updateFinalStep(true);
            setFinalStatus('success');
            setResponseCode(response.data.codigo);
            setResponseHash(response.data.hash);
            onQuestionsCompleted({
              hash,
              respuestas: answers,
              isApproved: true,
            });
          } else if (
            response.data.preguntas &&
            response.data.preguntas.length > 0
          ) {
            setNewQuestion(response.data.preguntas[0]);
          } else {
            updateFinalStep(true);
            setFinalStatus('error');
            setResponseCode(response.data.codigo);
            setResponseHash(response.data.hash);
            onQuestionsCompleted({
              hash,
              respuestas: answers,
              isApproved: false,
            });
          }
        }
      } catch (error) {
        console.error('Error al enviar cuestionario:', error);
        setAlertMessage('Hubo un problema al enviar el cuestionario.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const isAnswerSelected = () => {
    const currentQuestion = newQuestion || preguntas[currentQuestionIndex];
    return answers.some(
      (ans) => ans.id === currentQuestion.id && ans.respuesta !== ''
    );
  };

  const questionLabel = newQuestion
    ? `4. ${newQuestion.pregunta}`
    : `${currentQuestionIndex + 1}. ${
        preguntas[currentQuestionIndex].pregunta
      }`;

  const currentAlternativas = newQuestion
    ? newQuestion.alternativas
    : preguntas[currentQuestionIndex].alternativas;

  const questionId = newQuestion
    ? newQuestion.id
    : preguntas[currentQuestionIndex].id;

  const buttonLabel = isSubmitting ? 'Enviando...' : 'Siguiente';
  const isDisabled = !isAnswerSelected() || isSubmitting;
  const buttonClassName = `next-button${
    isDisabled ? ' next-button-disabled' : ''
  }`;

  return (
    <div className="questions-container">
      {isFinalStep ? (
        <FinalStepContent
          status={finalStatus}
          responseCode={responseCode}
          responseHash={responseHash}
        />
      ) : (
        <>
          {currentQuestionIndex === 0 && !newQuestion && (
            <div className="questions-text">
              <p className="first-question-title">Vamos a validar tu RUT.</p>
              <p className="first-question-text">
                Para continuar, primero debes verificar tu identidad
                respondiendo las siguientes preguntas de seguridad.
              </p>
            </div>
          )}
          {(currentQuestionIndex !== 0 || newQuestion) && (
            <img
              src={QuestionImg}
              alt="Cuestionario"
              className="question-image"
            />
          )}
          {alertMessage && <div className="alert-message">{alertMessage}</div>}
          <div
            className="question-item"
            data-page-id={`pregunta${currentQuestionIndex + 1}`}
          >
            <label className="question-label">{questionLabel}</label>
            <div className="alternatives-container">
              {currentAlternativas.map((alternativa, index) => {
                const isSelected = answers.some(
                  (ans) =>
                    ans.id === questionId && ans.respuesta === alternativa
                );
                return (
                  <div
                    key={index}
                    className={`alternative-item ${
                      isSelected ? "selected" : ""
                    }`}
                    onClick={() => handleAnswerChange(alternativa)}
                  >
                    <input
                      id="questionsInput"
                      type="radio"
                      name={`question-${questionId}`}
                      value={alternativa}
                      checked={isSelected}
                      readOnly
                      className="alternative-radio"
                    />
                    <label>{alternativa}</label>
                  </div>
                );
              })}
            </div>
          </div>

          {alertMessage && (
            <div className="alert-message-over-button">{alertMessage}</div>
          )}

          <div className="Button">
            <button
              id={`pregunta${currentQuestionIndex + 1}_siguiente`}
              onClick={handleNext}
              className={buttonClassName}
              disabled={isDisabled}
            >
              {buttonLabel}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default QuestionsComponent;
